import { type BaseProduct, baseFormat, defineFormatter } from "@frend-digital/centra/formatting";

type ExtendedProduct = BaseProduct & {
	var_color?: {
		value: string;
		hex: string;
	};
	var_display_color?: string;
	var_display_base_color?: string;
	pr_feature?: {
		value: string;
		image: {
			width: string;
			mimeType: string;
			type: string;
			url: string;
			height: string;
		};
	}[];
	pr_usp?: string[];
	pr_fit?: string;
	// "Size Table" attr — prod uses `pr_`, staging/dev uses `prd_`.
	pr_size_table_text?: string;
	prd_size_table_text?: string;
	pr_model_desc_text?: string;
	pr_main_material_text?: string;
	pr_upper_material_text?: string;
	pr_main_lining_text?: string;
	pr_main_padding_text?: string;
	pr_insole_fixing_text?: string;
	pr_midsole_text?: string;
	pr_outsole_text?: string;
	pr_membrane_text?: string;
	itemTable?: {
		desc?: string;
	};
};

export const formatProductCard = defineFormatter(
	(product: ExtendedProduct) => {
		// Find the canonical category:
		// Always prioritize the product's primary category from Centra (product.category)
		// This ensures we use the correct main category branch, not a secondary category
		const primaryCategoryId = product.category;
		const canonicalCategory = product.categories?.find((cat) => cat.category === primaryCategoryId);
		const canonicalCategoryId = canonicalCategory?.category ?? product.category;

		return {
			...baseFormat(product, {
				mediaKey: "1350x0",
			}),
			brand: product.brand,
			shortDescription: undefined,
			showAsOnSale:
				product.showAsOnSale || product.priceAsNumber !== product.priceBeforeDiscountAsNumber,
			color: product.var_color,
			baseColor: product.var_display_base_color,
			display_color: product.var_display_color,
			categories: product.categories,
			depictListingResultId: undefined as string | undefined,
			depictSearchResultId: undefined as string | undefined,
			/**
			 * Special variable to indicate if the product is on sale.
			 */
			isDiscounted: product.priceAsNumber !== product.priceBeforeDiscountAsNumber,
			depictProductId: product.items?.at(0)?.item?.split("-")?.[1],
			metaTitle: product.metaTitle,
			metaDescription: product.metaDescription,
			categoryId: canonicalCategoryId,
			centraProductNumber: product.productSku,
		};
	},
	{
		displayAsVariant: true,
	},
);

export const formatProductDetails = (product: ExtendedProduct) => {
return {
		...formatProductCard(product),
		shortDescription: product.excerpt,
		shortDescriptionHTML: product.excerptHtml,
		descriptionHTML: product.descriptionHtml,
		description: product.description,
		features: product.pr_feature,
		usp: product.pr_usp,
		materials: getMaterials(product),
		pr_fit: product.pr_fit,
		pr_model_desc: product.pr_model_desc_text,
		sizeTable: product.prd_size_table_text ?? product.pr_size_table_text,
		measurementChart: product.measurementChart,
		itemTable: product.itemTable,
		lowestPrice: product.lowestPrice,
	};
};

function getMaterials(product: ExtendedProduct) {
	const materials: { type: string; value: string }[] = [];
	if (product.pr_main_material_text) {
		materials.push({
			type: "Main material",
			value: product.pr_main_material_text,
		});
	}
	/* if (product.pr_main_lining_text) {
		materials.push({
			type: "Main lining",
			value: product.pr_main_lining_text,
		});
	} */
	if (product.pr_main_padding_text) {
		materials.push({
			type: "Main padding",
			value: product.pr_main_padding_text,
		});
	}
	if (product.pr_upper_material_text) {
		materials.push({
			type: "Upper material",
			value: product.pr_upper_material_text,
		});
	}
	if (product.pr_insole_fixing_text) {
		materials.push({
			type: "Insole fixing",
			value: product.pr_insole_fixing_text,
		});
	}
	if (product.pr_midsole_text) {
		materials.push({
			type: "Midsole",
			value: product.pr_midsole_text,
		});
	}
	if (product.pr_outsole_text) {
		materials.push({
			type: "Outsole",
			value: product.pr_outsole_text,
		});
	}
	if (product.pr_membrane_text) {
		materials.push({
			type: "Membrane",
			value: product.pr_membrane_text,
		});
	}
	return materials;
}

export type ProductCard = ReturnType<typeof formatProductCard>;
export type ProductDetails = ReturnType<typeof formatProductDetails>;

export const formatSeoProduct = defineFormatter((product: ExtendedProduct) => {
	// Always use the product's primary category from Centra for SEO purposes
	const primaryCategoryId = product.category;
	const canonicalCategory = product.categories?.find(
		(cat) => cat.category === primaryCategoryId,
	) ?? {
		category: product.category,
		uri: product.categoryUri,
	};
	const canonicalCategoryId = canonicalCategory.category ?? product.category;
	const canonicalCategoryUri = canonicalCategory.uri ?? product.categoryUri;

	return {
		displayItemId: product.product!,
		uri: product.uri!,
		name: product.name!,
		variantName: product.variantName!,
		canonicalCategoryId: canonicalCategoryId!,
		canonicalCategoryUri: canonicalCategoryUri,
		canonicalUri: canonicalCategoryUri ? `${canonicalCategoryUri}/${product.uri}` : undefined,
		lastModified: product.modifiedAt,
	};
});

export type SeoProduct = ReturnType<typeof formatSeoProduct>;
